import { COOKIE_CHOICE_MONTHS } from "../constants/legal";

/**
 * The visitor's answer to the cookie banner, and the one place it is kept.
 *
 * There are two answers today — essential only, or essential plus marketing
 * (the Meta Pixel) — so the record is one flag plus when it was given.
 *
 * **No answer means no.** Until the visitor clicks something, nothing optional
 * loads: consent is an act, never an absence (art. 4(5) of Legea 506/2004, GDPR
 * art. 4(11) and 7). `readConsent()` returning null is therefore treated
 * exactly like a refusal by everything that reads it — the only difference is
 * that the banner is still shown.
 *
 * Kept in localStorage rather than in a cookie: nothing on the server needs it,
 * and a cookie would ride along on every API request for no reason. A browser
 * that refuses storage still gets a working answer for the life of the page,
 * and is asked again on the next visit — the safe direction.
 */

export const CONSENT_STORAGE_KEY = "cookieConsent";

/**
 * Bump when the banner starts asking about something new — another category,
 * another partner. A consent covers only what the visitor was told about, so an
 * answer recorded under an older version is void and the banner asks again.
 */
export const CONSENT_VERSION = 1;

const OPEN_SETTINGS_EVENT = "easyplanner:cookie-settings";

const listeners = new Set();

// Used only when localStorage throws (blocked site data, a full quota): the
// answer still holds until the page is left.
let unsavedAnswer = null;

const expiryOf = (decidedAt) => {
  const expires = new Date(decidedAt);
  expires.setMonth(expires.getMonth() + COOKIE_CHOICE_MONTHS);
  return expires;
};

/**
 * The visitor's standing answer — `{ marketing, decidedAt }` — or null when
 * there is none: never asked, stored under an older version, malformed, or
 * older than COOKIE_CHOICE_MONTHS.
 */
export const readConsent = (now = new Date()) => {
  let raw;
  try {
    raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return unsavedAnswer;
  }
  if (!raw) return unsavedAnswer;

  try {
    const record = JSON.parse(raw);
    if (record?.version !== CONSENT_VERSION) return null;
    if (typeof record.marketing !== "boolean") return null;

    const decidedAt = new Date(record.decidedAt);
    if (Number.isNaN(decidedAt.getTime())) return null;
    if (expiryOf(decidedAt) <= now) return null;

    return { marketing: record.marketing, decidedAt: record.decidedAt };
  } catch {
    return null;
  }
};

const notify = () => {
  const consent = readConsent();
  listeners.forEach((listener) => listener(consent));
};

/** Records the visitor's answer and tells everyone listening. */
export const saveConsent = ({ marketing }, now = new Date()) => {
  const answer = { marketing: Boolean(marketing), decidedAt: now.toISOString() };
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ version: CONSENT_VERSION, ...answer }),
    );
    unsavedAnswer = null;
  } catch {
    unsavedAnswer = answer;
  }
  notify();
  return answer;
};

/**
 * Calls `listener` with the new answer whenever it changes — here, or in
 * another tab of the same site, so a withdrawal in one tab stops the pixel in
 * all of them. Returns the unsubscribe function.
 */
export const subscribeConsent = (listener) => {
  listeners.add(listener);
  const onStorage = (event) => {
    // A null key is localStorage.clear(), which takes the answer with it.
    if (event.key === CONSENT_STORAGE_KEY || event.key === null) {
      listener(readConsent());
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

/**
 * Opens the preferences dialog from anywhere — the footer's "Cookie settings"
 * link, the button on the Cookie Policy. Withdrawing has to be as easy as
 * consenting (GDPR art. 7(3)), so it is one click from every public page.
 */
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
};

/** Subscribes the dialog to `openCookieSettings`. Returns the unsubscribe function. */
export const onOpenCookieSettings = (handler) => {
  window.addEventListener(OPEN_SETTINGS_EVENT, handler);
  return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler);
};
